import React, { useCallback, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { createLocale } from 'locales';

import Checkbox from 'components-new/checkbox/checkbox';
import Typography from 'components-new/typography/typography';
import Button from 'components-new/button/button';

import {
  STATIC_FILES_URL,
  TERMS_AND_CONDITIONS_DOC,
} from 'constants/signup-flow-constants';

import { useFreelancerSignUp } from 'state/context/freelancer/FreelancerSignUp';
import useSubmitFreelancerApplication from 'state/remote/remote-freelancer/use-submit-freelancer-application';

import freelancerRoutes from '../freelancer-routes';
import FreelancerSignUpPageContainer from '../components/freelancer-sign-up-page-container';
import StepCard from '../components/step-card';

const localize = createLocale('freelancerFillInformationPageLocales');
const globalLocalize = createLocale('global');
const url = `${STATIC_FILES_URL}videos/signupflow/h2.mp4`;
const videoPreviewImageURL = `${window.STATIC_URL}assets/signupflow/h2.png`;

const FillInformation: React.FC = () => {
  const { signUpStatus, statusLoading } = useFreelancerSignUp();
  const { isLoading, submitApplication } = useSubmitFreelancerApplication();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [checkboxMyself, setCheckboxMyself] = useState<boolean>(false);
  const [checkboxTruthfully, setCheckboxTruthfully] = useState<boolean>(false);
  const [checkboxTermsAndConditions, setCheckboxTermsAndConditions] =
    useState<boolean>(false);
  const navigateToPersonalInformation = useCallback(() => {
    navigate(
      `${freelancerRoutes.personalInformation.path}?showVideoInstructions=true`,
    );
  }, [navigate]);
  const navigateToKVKInformation = useCallback(() => {
    navigate(
      `${freelancerRoutes.kvkInformation.path}?showVideoInstructions=true`,
    );
  }, [navigate]);
  const navigateToDocumentInformation = useCallback(() => {
    navigate(
      `${freelancerRoutes.documentsInformation.path}?showVideoInstructions=true`,
    );
  }, [navigate]);
  const navigateToDiplomasInformation = useCallback(() => {
    navigate(
      `${freelancerRoutes.diplomasInformation.path}?showVideoInstructions=true`,
    );
  }, [navigate]);
  const navigateToNextpage = useCallback(() => {
    if (signUpStatus?.personalInformation === 'not-completed')
      navigateToPersonalInformation();
    else if (signUpStatus?.KVKInformation === 'not-completed')
      navigateToKVKInformation();
    else if (signUpStatus?.documents === 'not-completed')
      navigateToDocumentInformation();
    else if (signUpStatus?.diplomas === 'not-completed')
      navigateToDiplomasInformation();
  }, [
    signUpStatus,
    navigateToPersonalInformation,
    navigateToKVKInformation,
    navigateToDocumentInformation,
    navigateToDiplomasInformation,
  ]);
  const onSubmitApplicationClicked = useCallback(() => {
    setSearchParams({ submitApplication: 'true' });
  }, [setSearchParams]);
  const onSendApplicationClicked = useCallback(() => {
    submitApplication();
    navigate(freelancerRoutes.submitApplication.path);
  }, [submitApplication, navigate]);
  const onCheckboxMyselfClicked = useCallback(() => {
    setCheckboxMyself((prevState) => !prevState);
  }, []);
  const onCheckboxTruthfullyClicked = useCallback(() => {
    setCheckboxTruthfully((prevState) => !prevState);
  }, []);
  const onCheckboxTermsAndConditionsClicked = useCallback(() => {
    setCheckboxTermsAndConditions((prevState) => !prevState);
  }, []);
  const enableBtn =
    checkboxMyself &&
    checkboxTermsAndConditions &&
    checkboxTruthfully &&
    !isLoading;

  if (statusLoading) return <></>;

  if (
    signUpStatus?.fillInformationComplete &&
    searchParams.get('submitApplication')
  )
    return (
      <FreelancerSignUpPageContainer
        headerTitle={localize('title')}
        showLogOut
        showHeader
        videoInstructions={{
          url,
          videoPreviewImageURL,
        }}
      >
        <>
          <Typography
            as="p"
            variant="body-medium"
            className="text-gray500 w-full mt-6 mb-8"
          >
            {localize('headlineCompleted')}
          </Typography>
          <div className="flex items-center my-4">
            <Checkbox
              variant={checkboxMyself ? 'checked' : 'unchecked'}
              onClick={onCheckboxMyselfClicked}
            />
            <Typography variant="body-medium" as="span" className="ml-4">
              {localize('checkboxMyself')}
            </Typography>
          </div>
          <div className="flex items-center my-4">
            <Checkbox
              variant={checkboxTruthfully ? 'checked' : 'unchecked'}
              onClick={onCheckboxTruthfullyClicked}
            />
            <Typography variant="body-medium" as="span" className="ml-4">
              {localize('checkboxTruthfully')}
            </Typography>
          </div>
          <div className="flex items-center my-4">
            <Checkbox
              variant={checkboxTermsAndConditions ? 'checked' : 'unchecked'}
              onClick={onCheckboxTermsAndConditionsClicked}
            />
            <Typography variant="body-medium" as="span" className="ml-4">
              {localize('checkboxTermsAndConditions1')}
              <a
                href={TERMS_AND_CONDITIONS_DOC}
                target="_blank"
                rel="noreferrer"
                className="text-current text-purple font-semibold"
              >
                {localize('termsAndConditions')}
              </a>
              {localize('checkboxTermsAndConditions2')}
            </Typography>
          </div>
          <Button
            loading={isLoading}
            label={localize('submitApplication')}
            variant="primary"
            className="w-full justify-center mt-10"
            onClick={onSendApplicationClicked}
            disabled={!enableBtn}
          />
        </>
      </FreelancerSignUpPageContainer>
    );

  return (
    <FreelancerSignUpPageContainer
      headerTitle={localize('title')}
      showLogOut
      showHeader
      videoInstructions={{
        url,
        videoPreviewImageURL,
      }}
    >
      <>
        <Typography
          as="p"
          variant="body-medium"
          className="text-gray500 w-full mt-6 mb-8"
        >
          {localize('headline')}
        </Typography>
        <StepCard
          className="mt-0"
          title={localize('personalInformationStep')}
          status={signUpStatus?.personalInformation}
          icon="user"
          onClick={navigateToPersonalInformation}
        />
        <StepCard
          title={localize('kvkInformationStep')}
          status={signUpStatus?.KVKInformation}
          icon="euro"
          onClick={navigateToKVKInformation}
        />
        <StepCard
          title={localize('documentsStep')}
          status={signUpStatus?.documents}
          icon="attachment"
          onClick={navigateToDocumentInformation}
        />
        <StepCard
          title={localize('diplomasStep')}
          status={signUpStatus?.diplomas}
          icon="certificate1"
          onClick={navigateToDiplomasInformation}
        />
        {signUpStatus?.fillInformationComplete && (
          <Button
            label={localize('submitApplication')}
            variant="primary"
            className="w-full justify-center my-10"
            onClick={onSubmitApplicationClicked}
          />
        )}
        {!signUpStatus?.fillInformationComplete && (
          <Button
            loading={isLoading}
            disabled={isLoading}
            label={globalLocalize('continue')}
            variant="primary"
            className="w-full justify-center my-10"
            onClick={navigateToNextpage}
          />
        )}
      </>
    </FreelancerSignUpPageContainer>
  );
};
export default FillInformation;
